import { BaseModel, type RelationConfig } from './base';
import { dateYMDRelation, type DateYMD } from '../lib/dates';
import { plugin as $date } from '~/plugins/date';

import { ConfirmationRequestService } from './booking';
import { Reservation } from './reservation';
import { Service } from './service';

// Enums

export enum RecurrenceIntervalType {
  week = 'week',
  month = 'month',
  year = 'year',
}

export const RecurrenceIntervalTypeLabels: Record<RecurrenceIntervalType, string> = {
  [RecurrenceIntervalType.week]: 'datetime.labels.week',
  [RecurrenceIntervalType.month]: 'datetime.labels.month',
  [RecurrenceIntervalType.year]: 'datetime.labels.year',
};

export enum RecurrenceDays {
  monday = 1,
  tuesday = 2,
  wednesday = 3,
  thursday = 4,
  friday = 5,
  saturday = 6,
  sunday = 7,
}

export const RecurrenceDaysLabels: Record<RecurrenceDays, string> = {
  [RecurrenceDays.monday]: 'datetime.days.monday.long',
  [RecurrenceDays.tuesday]: 'datetime.days.tuesday.long',
  [RecurrenceDays.wednesday]: 'datetime.days.wednesday.long',
  [RecurrenceDays.thursday]: 'datetime.days.thursday.long',
  [RecurrenceDays.friday]: 'datetime.days.friday.long',
  [RecurrenceDays.saturday]: 'datetime.days.saturday.long',
  [RecurrenceDays.sunday]: 'datetime.days.sunday.long',
};

export enum RecurrenceMonths {
  january = 1,
  february = 2,
  march = 3,
  april = 4,
  may = 5,
  june = 6,
  july = 7,
  august = 8,
  september = 9,
  october = 10,
  november = 11,
  december = 12,
}

export const RecurrenceMonthsLabels: Record<RecurrenceMonths, string> = {
  [RecurrenceMonths.january]: 'datetime.months.january',
  [RecurrenceMonths.february]: 'datetime.months.february',
  [RecurrenceMonths.march]: 'datetime.months.march',
  [RecurrenceMonths.april]: 'datetime.months.april',
  [RecurrenceMonths.may]: 'datetime.months.may',
  [RecurrenceMonths.june]: 'datetime.months.june',
  [RecurrenceMonths.july]: 'datetime.months.july',
  [RecurrenceMonths.august]: 'datetime.months.august',
  [RecurrenceMonths.september]: 'datetime.months.september',
  [RecurrenceMonths.october]: 'datetime.months.october',
  [RecurrenceMonths.november]: 'datetime.months.november',
  [RecurrenceMonths.december]: 'datetime.months.december',
};

export enum RecurrenceIntervalMonthType {
  day = 'day',
  date = 'date',
  last = 'last',
}

export const RecurrenceIntervalMonthTypeLabels: Record<RecurrenceIntervalMonthType, string> = {
  [RecurrenceIntervalMonthType.day]: 'api.models.recurrence.dayOfWeek',
  [RecurrenceIntervalMonthType.date]: 'api.models.recurrence.dayOfMonth',
  [RecurrenceIntervalMonthType.last]: 'api.models.recurrence.lastDayOfMonth',
};

export enum RecurrenceEndingType {
  date = 'date',
  repeats = 'repeats',
}

export const RecurrenceEndingTypeLabels: Record<RecurrenceEndingType, string> = {
  [RecurrenceEndingType.date]: 'api.models.recurrence.onDate',
  [RecurrenceEndingType.repeats]: 'reservation.afterNumberOfRepeats',
};

export enum RecurrenceWeeks {
  first = 1,
  second = 2,
  third = 3,
  fourth = 4,
}

export const RecurrenceWeeksLabels: Record<RecurrenceWeeks, string> = {
  [RecurrenceWeeks.first]: 'api.models.recurrence.first',
  [RecurrenceWeeks.second]: 'api.models.recurrence.second',
  [RecurrenceWeeks.third]: 'api.models.recurrence.third',
  [RecurrenceWeeks.fourth]: 'api.models.recurrence.fourth',
};

export enum RecurrenceConflict {
  skip = 'skip',
  doubleBook = 'doubleBook',
  delete = 'delete',
}

export const RecurrenceConflictLabels: Record<RecurrenceConflict, string> = {
  [RecurrenceConflict.skip]: 'api.models.recurrence.skip',
  [RecurrenceConflict.doubleBook]: 'api.models.recurrence.doubleBook',
  [RecurrenceConflict.delete]: 'general.delete',
};

export enum RecurrenceCustomerAction {
  book = 'book',
  cancel = 'cancel',
  delete = 'delete',
}

export const RecurrenceCustomerActionLabels: Record<RecurrenceCustomerAction, string> = {
  [RecurrenceCustomerAction.book]: 'api.models.recurrence.book',
  [RecurrenceCustomerAction.cancel]: 'general.cancel',
  [RecurrenceCustomerAction.delete]: 'general.delete',
};

// Helper Classes

export class RecurrenceRepeatOptions extends BaseModel {
  weeksOfMonth!: RecurrenceWeeks[];
  lastDayOfMonth!: boolean;
}

export class RecurrenceRepeatOn extends BaseModel {
  values!: any[];
  options!: RecurrenceRepeatOptions | null;

  static override relations(): RelationConfig {
    return {
      options: { type: RecurrenceRepeatOptions },
    };
  }
}

export class RecurrenceRepeat extends BaseModel {
  interval!: RecurrenceIntervalType;
  frequency!: number;
  on!: RecurrenceRepeatOn;

  static override relations(): RelationConfig {
    return {
      on: { type: RecurrenceRepeatOn },
    };
  }
}

export class RecurrenceEnding extends BaseModel {
  date!: DateYMD | null;
  numberOfRepeats!: number | null;

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
    };
  }
}

export class RecurrenceSelectedDate extends BaseModel {
  date!: number;
  options!: RecurrenceConflict[] | null;
  option!: RecurrenceConflict | null;

  get hasConflicts(): boolean {
    return !!this.options;
  }

  get conflictOptions(): { value: RecurrenceConflict; text: string }[] | null {
    const { $i18n } = useNuxtApp();
    const t = $i18n.t as (key: string) => string;

    return this.options?.map((option) => ({
      value: option,
      text: RecurrenceConflictLabels[option] ? t(RecurrenceConflictLabels[option]) : option,
    })) ?? null;
  }
}

export class RecurrenceDateWithConflict extends BaseModel {
  date!: number;
  option!: RecurrenceConflict | null;
}

// Main Model

export class Recurrence extends BaseModel {
  repeat!: RecurrenceRepeat;
  ending!: RecurrenceEnding;
  selectedDates!: RecurrenceSelectedDate[];
  currentDates!: RecurrenceSelectedDate[];
  datesWithConflicts!: RecurrenceDateWithConflict[];
  services!: ConfirmationRequestService[];
  recurrenceNumber!: number;
  recurrenceCount!: number;

  static override relations(): RelationConfig {
    return {
      repeat: { type: RecurrenceRepeat },
      ending: { type: RecurrenceEnding },
      selectedDates: { type: RecurrenceSelectedDate },
      currentDates: { type: RecurrenceSelectedDate },
      datesWithConflicts: { type: RecurrenceDateWithConflict },
      services: { type: ConfirmationRequestService },
    };
  }

  // Existing occurrences that no longer fit the recurrence pattern — the api
  // flags these with resolution options and removes them (delete by default)
  // when the recurrence is saved.
  get removedDates(): RecurrenceSelectedDate[] {
    return (this.currentDates ?? []).filter((date) => !!date.options);
  }

  // New occurrences introduced by the edited pattern — selected dates that are
  // not already present among the current occurrences.
  get addedDates(): RecurrenceSelectedDate[] {
    const existing = new Set((this.currentDates ?? []).map((date) => date.date));
    return (this.selectedDates ?? []).filter((date) => !existing.has(date.date));
  }

  get label(): string {
    const { $i18n } = useNuxtApp();
    const t = $i18n.t as (key: string, params?: Record<string, any>) => string;

    const frequency = this.repeat?.frequency;

    let interval: string | undefined;
    let on: string | undefined;
    let ending: string | undefined;

    if (frequency > 1) {
      interval = t('datetime.increments.weeks', { weeks: frequency, count: frequency }).toLowerCase();
    } else {
      interval = t(RecurrenceIntervalTypeLabels[this.repeat?.interval]).toLowerCase();
    }

    if (this.isRecurrenceIntervalWeek) {
      const dayValue = Number(this.repeat.on.values[0]);
      const day = t(RecurrenceDaysLabels[dayValue as RecurrenceDays]);
      on = t('api.models.recurrence.onDay', { day });
    }

    if (this.isRecurrenceIntervalMonth) {
      if (this.isRecurrenceMonthTypeDay) {
        const weekValue = this.repeat.on.options!.weeksOfMonth![0];
        const ordinal = t(RecurrenceWeeksLabels[weekValue as RecurrenceWeeks]);
        const dayValue = this.repeat.on.values[0];
        const day = t(RecurrenceDaysLabels[dayValue as RecurrenceDays]);
        on = t('api.models.recurrence.onOrdinalDay', { ordinal, day });
      }

      if (this.isRecurrenceMonthTypeDate) {
        const day = this.repeat.on.values[0];
        const ordinal = $date.getOrdinal(day);
        on = t('api.models.recurrence.onOrdinalDayOfTheMonth', { ordinal });
      }

      if (this.isRecurrenceMonthTypeLast) {
        on = t('api.models.recurrence.onLastDayOfTheMonth');
      }
    }

    if (this.isRecurrenceIntervalYear) {
      const dateVal = this.repeat.on.values[0].date;
      const monthValue = this.repeat.on.values[0].month;
      const month = t(RecurrenceMonthsLabels[monthValue as RecurrenceMonths]);
      const ordinal = $date.getOrdinal(dateVal);
      on = t('api.models.recurrence.onMonthOrdinal', { month, ordinal });
    }

    if (this.isEndingOnDate) {
      const date = $date.convert(this.ending.date as any, undefined, $date.presets.long);
      ending = t('api.models.recurrence.endingOnDate', { date });
    }

    if (this.isEndingAfterRepeats) {
      const repeats = this.ending.numberOfRepeats;
      ending = t('api.models.recurrence.endingAfterRepeats', { repeats, count: repeats });
    }

    return t('api.models.recurrence.label', { interval, on, ending });
  }

  get isEndingOnDate(): boolean {
    return !!(this.ending?.date as any)?.value;
  }

  get isEndingAfterRepeats(): boolean {
    return !!this.ending?.numberOfRepeats;
  }

  get isRecurrenceIntervalWeek(): boolean {
    return this.repeat?.interval === RecurrenceIntervalType.week;
  }

  get isRecurrenceIntervalMonth(): boolean {
    return this.repeat?.interval === RecurrenceIntervalType.month;
  }

  get isRecurrenceIntervalYear(): boolean {
    return this.repeat?.interval === RecurrenceIntervalType.year;
  }

  get isRecurrenceMonthTypeDate(): boolean {
    return !this.isRecurrenceMonthTypeDay && !this.isRecurrenceMonthTypeLast;
  }

  get isRecurrenceMonthTypeDay(): boolean {
    return !!this.repeat?.on?.options?.weeksOfMonth;
  }

  get isRecurrenceMonthTypeLast(): boolean {
    return !!this.repeat?.on?.options?.lastDayOfMonth;
  }
}

// Preview response for BookingService.previewRecurrence

export class RecurrencePreview extends BaseModel {
  recurrence!: Recurrence;

  static override relations(): RelationConfig {
    return {
      recurrence: { type: Recurrence },
    };
  }
}

// RecurrenceRequest for creating/editing recurrences

export class RecurrenceRequest extends BaseModel {
  reservation!: Reservation | null;
  services!: Service[] | null;
  recurrence!: Recurrence | null;

  static override relations(): RelationConfig {
    return {
      reservation: { type: Reservation },
      services: { type: Service },
      recurrence: { type: Recurrence },
    };
  }

  override toJSON(): Record<string, any> {
    return {
      recurrence: this.recurrence ? this.recurrence.toJSON() : null,
      services: this.services ? this.services.map(service => service.toJSON()) : null,
      idReservation: this.reservation ? (this.reservation as any).idReservation : null,
    };
  }

  static create({
    recurrence,
    reservation,
    services,
  }: {
    recurrence?: any;
    reservation?: Reservation;
    services?: Service[];
  } = {}): RecurrenceRequest {
    const request = new RecurrenceRequest();

    request.reservation = reservation ?? null;
    request.services = services ?? null;

    if (recurrence) {
      const object: any = {
        repeat: {} as any,
        ending: {} as any,
      };

      object.repeat.frequency = recurrence.repeat.interval === RecurrenceIntervalType.year
        ? 1
        : recurrence.repeat.frequency;

      if (recurrence.repeat.interval === RecurrenceIntervalType.week) {
        object.repeat.interval = RecurrenceIntervalType.week;
        object.repeat.on = {
          values: recurrence.repeat.on.week.days,
        };
      }

      if (recurrence.repeat.interval === RecurrenceIntervalType.month) {
        object.repeat.interval = RecurrenceIntervalType.month;

        if (recurrence.repeat.on.month.type === RecurrenceIntervalMonthType.day) {
          object.repeat.on = {
            options: {
              weeksOfMonth: recurrence.repeat.on.month.weeks
                .map((week: any) => week.value ?? week),
            },
            values: recurrence.repeat.on.month.days,
          };
        }

        if (recurrence.repeat.on.month.type === RecurrenceIntervalMonthType.date) {
          object.repeat.on = {
            values: recurrence.repeat.on.month.dates,
          };
        }

        if (recurrence.repeat.on.month.type === RecurrenceIntervalMonthType.last) {
          object.repeat.on = {
            options: { lastDayOfMonth: true },
          };
        }
      }

      if (recurrence.repeat.interval === RecurrenceIntervalType.year) {
        object.repeat.interval = RecurrenceIntervalType.year;
        const date = recurrence.repeat.on.year.date;
        object.repeat.on = {
          values: [
            { date: date.date, month: date.month.value ?? date.month },
          ],
        };
      }

      if (recurrence.ending.type === RecurrenceEndingType.date) {
        object.ending.date = recurrence.ending.date;
      }

      if (recurrence.ending.type === RecurrenceEndingType.repeats) {
        object.ending.numberOfRepeats = recurrence.ending.repeats;
      }

      if (recurrence.datesWithConflicts) {
        object.datesWithConflicts = recurrence.datesWithConflicts;
      }

      request.recurrence = new Recurrence(object);
    }

    return request;
  }
}
